import {
  BankOpenClientPacket,
  BarberOpenClientPacket,
  type CharacterMapInfo,
  CitizenOpenClientPacket,
  Direction,
  GuildOpenClientPacket,
  ItemSubtype,
  MarriageOpenClientPacket,
  MapTileSpec,
  type NpcMapInfo,
  NpcType,
  PriestOpenClientPacket,
  QuestUseClientPacket,
  ShopOpenClientPacket,
  SitState,
  StatSkillOpenClientPacket,
} from 'eolib';
import type { Client } from '@/client';
import {isCompanion,ownCompanion,commandCompanion,commandCompanionPlayer,tickCompanionPlayer} from '@/summoning';
import { ATTACK_TICKS } from '@/consts';
import { EOResourceID } from '@/edf';
import { SpellTarget } from '@/game-state';
import {
  CharacterAttackAnimation,
  CharacterRangedAttackAnimation,
  CharacterWalkAnimation,
  ChatBubble,
  NpcDeathAnimation,
} from '@/render';
import { SfxId } from '@/sfx';
import type { Vector2 } from '@/vector';
import { getTimestamp } from './movement-controller';


const RON_THE_SMITH_NPC_ID = 479;
import {dungeonNpcInactive} from '@/dungeon-contracts';
export class NpcController {
  private client: Client;
  interactNpcIndex = 0;
  selectedCombatNpcIndex: number | null = null;
  queuedNpcChats: Map<number, string[]> = new Map();
  private autoAttackNpcIndex: number | null = null;
  private autoAttackPathDestination: string | null = null;
  private autoAttackCooldownTicks = 0;

  constructor(client: Client) {
    this.client = client;
  }

  clickNpc(npc: NpcMapInfo): void {
    if(isCompanion(this.client,npc.index)){
      this.cancelAutoAttack();
      if(this.client.spellController.selectedSpellId)this.client.spellController.queueSelectedSpellForNpc(npc.index);
      return;
    }
    if(dungeonNpcInactive(this.client,npc.index)){this.client.toastController.showWarning('Clear the current room to unlock this encounter.');return;}
    const record = this.client.getEnfRecordById(npc.id);
    if (!record) {
      return;
    }

    switch (record.type) {
      case NpcType.Quest: {
        // A pending click-to-walk path must not close the conversation on its
        // next step. Talking is a new action, not an extension of that path.
        this.cancelAutoAttack();
        this.client.movementController.autoWalkPath = [];
        const packet = new QuestUseClientPacket();
        packet.npcIndex = npc.index;
        packet.questId = 0;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Bank: {
        const packet = new BankOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Shop: {
        this.client.shopController.pendingShopBehaviorId = record.behaviorId;
        const packet = new ShopOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Barber: {
        const packet = new BarberOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Guild: {
        const packet = new GuildOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Inn: {
        const packet = new CitizenOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Lawyer: {
        const packet = new MarriageOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Priest: {
        const packet = new PriestOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Trainer: {
        const packet = new StatSkillOpenClientPacket();
        packet.npcIndex = npc.index;
        this.client.bus!.send(packet);
        break;
      }
      case NpcType.Aggressive:
      case NpcType.Passive: {
        if (this.client.spellController.hasPendingSpellAction()) {
          this.stopAutoAttack(false);
          this.client.movementController.autoWalkPath = [];
          return;
        }

        if (this.client.spellController.selectedSpellId) {
          this.stopAutoAttack(false);
          this.interactNpcIndex = npc.index;
          if (this.client.spellController.canSelectedSpellTargetNpc()) {
            this.client.spellController.queueSelectedSpellForNpc(npc.index);
          } else {
            this.client.spellController.cancelTargeting(true);
            this.client.toastController.showWarning(
              'That spell cannot target monsters.',
            );
          }
          break;
        }

        if(ownCompanion(this.client)){
          this.stopAutoAttack(true);this.selectedCombatNpcIndex=npc.index;
          commandCompanion(this.client,'summon_target',npc.index);return;
        }
        this.startAutoAttack(npc);
        return;
      }
      default:
        return;
    }

    this.interactNpcIndex = npc.index;
  }

  cancelAutoAttack(): void {
    this.stopAutoAttack(false);
  }

  clearAllTargets(): void {
    this.stopAutoAttack(true);
    this.selectedCombatNpcIndex = null;
    this.interactNpcIndex = 0;
    this.client.spellController.cancelTargeting();
  }

  clearCombatTarget(): void {
    if (
      this.client.spellController.spellTarget === SpellTarget.Npc &&
      this.client.spellController.spellTargetId === this.selectedCombatNpcIndex
    ) {
      this.client.spellController.cancelTargeting();
    }
    this.selectedCombatNpcIndex = null;
  }

  private startAutoAttack(npc: NpcMapInfo): void {
    this.autoAttackNpcIndex = npc.index;
    this.interactNpcIndex = npc.index;
    this.autoAttackCooldownTicks = 0;
    this.autoAttackPathDestination = null;
    this.client.movementController.autoWalkPath = [];
    if (this.client.spellController.canSelectedSpellTargetNpc()) {
      this.client.spellController.queueSelectedSpellForNpc(npc.index);
    }
  }

  private stopAutoAttack(clearOwnedPath: boolean): void {
    if (
      clearOwnedPath &&
      this.autoAttackPathDestination &&
      this.pathEndsAt(this.client.movementController.autoWalkPath, this.autoAttackPathDestination)
    ) {
      this.client.movementController.autoWalkPath = [];
    }

    this.autoAttackNpcIndex = null;
    this.autoAttackPathDestination = null;
    this.autoAttackCooldownTicks = 0;
  }

  cycleCombatTarget(): void {
    if (this.selectedCombatNpcIndex !== null) {
      this.stopAutoAttack(true);
      this.clearCombatTarget();
      this.interactNpcIndex = 0;
      this.client.toastController.show('Target cleared.');
      return;
    }

    const playerCoords = this.client.getPlayerCoords();
    const targets = this.client.nearby.npcs
      .filter(
        (npc) => this.isCombatNpc(npc) && !this.isNpcDying(npc) &&
          Math.abs(npc.coords.x - playerCoords.x) + Math.abs(npc.coords.y - playerCoords.y) <= 20,
      )
      .sort((a, b) => {
        const aDistance =
          Math.abs(a.coords.x - playerCoords.x) +
          Math.abs(a.coords.y - playerCoords.y);
        const bDistance =
          Math.abs(b.coords.x - playerCoords.x) +
          Math.abs(b.coords.y - playerCoords.y);
        return aDistance - bDistance || a.index - b.index;
      });

    if (!targets.length) {
      this.clearCombatTarget();
      this.client.toastController.showWarning('No target nearby.');
      return;
    }

    const target = targets[0];
    const record = this.client.getEnfRecordById(target.id);

    this.selectedCombatNpcIndex = target.index;
    this.interactNpcIndex = target.index;
    this.faceTargetIfIdle(target);

    this.client.toastController.show(`Target: ${record?.name || 'NPC'}`);
  }

  private faceTargetIfIdle(npc: NpcMapInfo): void {
    const character = this.client.getPlayerCharacter();
    if (!character) {
      return;
    }

    const animation = this.client.animationController.characterAnimations.get(
      character.playerId,
    );
    if (animation instanceof CharacterWalkAnimation) {
      return;
    }

    const coords = this.client.getPlayerCoords();
    const diffX = npc.coords.x - coords.x;
    const diffY = npc.coords.y - coords.y;
    let direction: Direction | null = null;

    if (diffX === 0 && diffY !== 0) {
      direction = diffY > 0 ? Direction.Down : Direction.Up;
    } else if (diffY === 0 && diffX !== 0) {
      direction = diffX > 0 ? Direction.Right : Direction.Left;
    }

    if (direction !== null && character.direction !== direction) {
      character.direction = direction;
      this.client.movementController.face(direction);
    }
  }

  private tickAutoAttack(): void {
    this.autoAttackCooldownTicks = Math.max(
      this.autoAttackCooldownTicks - 1,
      0,
    );

    if (this.autoAttackNpcIndex === null) {
      return;
    }

    if (
      this.client.spellController.selectedSpellId ||
      this.client.spellController.hasPendingSpellAction()
    ) {
      this.stopAutoAttack(true);
      return;
    }

    const npc = this.client.getNpcByIndex(this.autoAttackNpcIndex);
    if (!npc || !this.isCombatNpc(npc) || this.isNpcDying(npc)) {
      this.stopAutoAttack(true);
      return;
    }

    const character = this.client.getPlayerCharacter();
    if (!character || character.sitState !== SitState.Stand) {
      this.stopAutoAttack(false);
      return;
    }

    const animation = this.client.animationController.characterAnimations.get(
      character.playerId,
    );

    if (animation instanceof CharacterWalkAnimation) {
      return;
    }

    const currentPath = this.client.movementController.autoWalkPath;
    if (
      currentPath.length &&
      this.autoAttackPathDestination &&
      !this.pathEndsAt(currentPath, this.autoAttackPathDestination)
    ) {
      this.stopAutoAttack(false);
      return;
    }

    const current = this.client.getPlayerCoords();
    const attackDirection = this.getAdjacentAttackDirection(current, npc.coords);
    if (attackDirection === null) {
      if (
        currentPath.length &&
        this.autoAttackPathDestination &&
        this.pathEndsAt(currentPath, this.autoAttackPathDestination) &&
        this.isAdjacentToNpc(this.autoAttackPathDestination, npc)
      ) {
        return;
      }

      const path = this.findPathAdjacentToNpc(npc);
      if (!path.length) {
        this.autoAttackPathDestination = null;
        return;
      }

      this.autoAttackPathDestination = this.coordsKey(path[path.length - 1]);
      this.client.movementController.autoWalkPath = path;
      return;
    }

    this.autoAttackPathDestination = null;
    this.client.movementController.autoWalkPath = [];

    if (animation?.ticks || this.autoAttackCooldownTicks > 0) {
      return;
    }

    const metadata = this.client.getWeaponMetadata(character.equipment.weapon);
    if (
      (
        metadata.ranged
      ) &&
      metadata.sfx[0] !== SfxId.Gun &&
      metadata.sfx[0] !== SfxId.Harp1 &&
      metadata.sfx[0] !== SfxId.Guitar1
    ) {
      const shield = this.client.equipment.shield;
      const record = this.client.getEifRecordById(shield);
      if (!record || record.subtype !== ItemSubtype.Arrows) {
        this.client.audioController.playById(SfxId.NoArrows);
        this.client.toastController.showWarning(
          this.client.getResourceString(
            EOResourceID.STATUS_LABEL_YOU_HAVE_NO_ARROWS,
          ),
        );
        this.stopAutoAttack(false);
        return;
      }
    }

    if (character.direction !== attackDirection) {
      character.direction = attackDirection;
      this.client.movementController.face(attackDirection);
    }

    this.client.animationController.characterAnimations.set(
      character.playerId,
      metadata.ranged
        ? new CharacterRangedAttackAnimation()
        : new CharacterAttackAnimation(),
    );
    this.client.movementController.attack(attackDirection, getTimestamp());
    this.autoAttackCooldownTicks = ATTACK_TICKS;
  }

  private isCombatNpc(npc: NpcMapInfo): boolean {
    if(isCompanion(this.client,npc.index))return false;
    if (npc.id === RON_THE_SMITH_NPC_ID) {
      return false;
    }

    const record = this.client.getEnfRecordById(npc.id);
    return !!record && [NpcType.Aggressive, NpcType.Passive].includes(record.type);
  }

  private isNpcDying(npc: NpcMapInfo): boolean {
    const animation = this.client.animationController.npcAnimations.get(
      npc.index,
    );
    return animation instanceof NpcDeathAnimation;
  }

  private findPathAdjacentToNpc(npc: NpcMapInfo): Vector2[] {
    const candidates: Vector2[] = [];
    for (let distance = this.deathBowRange(); distance >= 1; distance--) {
      candidates.push(
        { x: npc.coords.x, y: npc.coords.y - distance },
        { x: npc.coords.x + distance, y: npc.coords.y },
        { x: npc.coords.x, y: npc.coords.y + distance },
        { x: npc.coords.x - distance, y: npc.coords.y },
      );
    }

    let bestPath: Vector2[] = [];
    for (const candidate of candidates) {
      if (!this.client.mapController.canWalk(candidate, true) || this.getAdjacentAttackDirection(candidate, npc.coords) === null) {
        continue;
      }

      const path = this.client.mapController.findPathTo(candidate);
      if (!path.length) {
        continue;
      }

      if (!bestPath.length || path.length < bestPath.length) {
        bestPath = path;
      }
    }

    return bestPath;
  }

  private getAdjacentAttackDirection(from: Vector2, to: Vector2): Direction | null {
    const diffX = to.x - from.x;
    const diffY = to.y - from.y;

    const distance = Math.abs(diffX) + Math.abs(diffY);
    if (distance < 1 || distance > this.deathBowRange() || (diffX !== 0 && diffY !== 0)) return null;
    if (this.deathBowRange() > 1) {
      // Match server attack collision; NPC occupancy is not a terrain wall.
      const blocked = [MapTileSpec.Wall, MapTileSpec.ChairDown, MapTileSpec.ChairLeft,
        MapTileSpec.ChairRight, MapTileSpec.ChairUp, MapTileSpec.ChairDownRight,
        MapTileSpec.ChairUpLeft, MapTileSpec.ChairAll, MapTileSpec.Chest,
        MapTileSpec.BankVault, MapTileSpec.Board1, MapTileSpec.Board2,
        MapTileSpec.Board3, MapTileSpec.Board4, MapTileSpec.Board5,
        MapTileSpec.Board6, MapTileSpec.Board7, MapTileSpec.Board8, MapTileSpec.Jukebox];
      for (let step = 1; step <= distance; step++) {
        const coords = {x: from.x + Math.sign(diffX) * step, y: from.y + Math.sign(diffY) * step};
        if (coords.x < 0 || coords.y < 0 || coords.x > this.client.map!.width || coords.y > this.client.map!.height) return null;
        const spec = this.client.mapRenderer.getTileSpecAt(coords);
        if (spec !== undefined && blocked.includes(spec)) return null;
      }
    }

    if (diffX > 0) return Direction.Right;
    if (diffX < 0) return Direction.Left;
    if (diffY > 0) return Direction.Down;
    return Direction.Up;
  }

  private deathBowRange(): number {
    // Equipment on the rendered character contains a graphic ID, not an item ID.
    return this.client.getPlayerCharacter()?.equipment.weapon === 93 ? 5 : 1;
  }

  private coordsKey(coords: Vector2): string {
    return `${coords.x},${coords.y}`;
  }

  private pathEndsAt(path: Vector2[], key: string): boolean {
    const destination = path[path.length - 1];
    return !!destination && this.coordsKey(destination) === key;
  }

  private isAdjacentToNpc(key: string, npc: NpcMapInfo): boolean {
    const [x, y] = key.split(',').map(Number);
    return this.getAdjacentAttackDirection({x, y}, npc.coords) !== null;
  }

  clickCharacter(character: CharacterMapInfo): void {
    if(!this.client.spellController.selectedSpellId&&commandCompanionPlayer(this.client,character.playerId)){
      this.stopAutoAttack(true);this.client.movementController.autoWalkPath=[];return;
    }
    if (
      !this.client.spellController.selectedSpellId ||
      this.client.spellController.queuedSpellId > 0 ||
      this.client.spellController.spellCooldownTicks > 0
    ) {
      return;
    }

    this.client.spellController.spellTarget = SpellTarget.Player;
    this.client.spellController.spellTargetId = character.playerId;
    this.client.spellController.queuedSpellId =
      this.client.spellController.selectedSpellId;
  }

  private tickSelectedCombatTarget(): void {
    if (this.selectedCombatNpcIndex === null) {
      return;
    }

    const npc = this.client.getNpcByIndex(this.selectedCombatNpcIndex);
    if (npc && this.isCombatNpc(npc) && !this.isNpcDying(npc)) {
      return;
    }

    this.clearCombatTarget();
  }

  tick(): void {
    tickCompanionPlayer(this.client);
    if (this.client.worldLoading) return;
    this.tickSelectedCombatTarget();
    this.tickAutoAttack();

    const emptyQueuedNpcChats: number[] = [];
    for (const [index, messages] of this.queuedNpcChats) {
      const existingChat = this.client.animationController.npcChats.get(index);
      if (existingChat) {
        continue;
      }

      const npc = this.client.getNpcByIndex(index);
      if (!npc) {
        emptyQueuedNpcChats.push(index);
        continue;
      }

      const record = this.client.getEnfRecordById(npc.id);
      if (!record) {
        emptyQueuedNpcChats.push(index);
        continue;
      }

      this.client.animationController.npcChats.set(
        index,
        new ChatBubble(this.client.sans11, messages[0]),
      );

      if (messages.length > 1) {
        messages.splice(0, 1);
      } else {
        emptyQueuedNpcChats.push(index);
      }
    }
    for (const index of emptyQueuedNpcChats) {
      this.queuedNpcChats.delete(index);
    }
  }
}
